import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Loader2, Zap } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

interface CreditBalanceResp {
  ai: {
    mode: string;
    payer: string;
    workspace: { balanceCents: number } | null;
    perUser: { currentBalance: number; rolloverBalance: number } | null;
  };
}

interface Pack {
  priceCents: number;
  creditsCents: number;
  label: string;
  badge?: string;
}

// 1 credit = $0.10 = 10¢. Balances/packs are stored as dollar-value cents, so
// the credit count is cents ÷ 10. Display floors to the nearest whole credit
// (internal fractional tracking is preserved; only the display rounds down).
function formatCr(cents: number) {
  return Math.floor(cents / 10).toLocaleString() + " cr";
}

interface TopUpDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

function TopUpDialog({ open, onOpenChange }: TopUpDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState<number | null>(null);

  const { data: packs } = useQuery<Pack[]>({
    queryKey: ["/api/ai-credits/packs"],
    queryFn: () => fetch("/api/ai-credits/packs").then((r) => r.json()),
    staleTime: Infinity,
    enabled: open,
  });

  const handlePurchase = async (pack: Pack) => {
    setLoading(pack.priceCents);
    try {
      const r = await apiRequest("POST", "/api/ai-credits/checkout-session", { priceCents: pack.priceCents });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error((err as any).message || "Checkout failed");
      }
      const { url } = await r.json();
      if (url) window.location.href = url;
    } catch (e: any) {
      toast({ title: "Checkout failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm" data-testid="dialog-ai-topup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Top up AI Credits
          </DialogTitle>
          <DialogDescription>
            Credits are shared across your workspace. Purchases are instant.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {(!packs || packs.length === 0) ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            packs.map((pack) => (
              <button
                key={pack.priceCents}
                onClick={() => handlePurchase(pack)}
                disabled={loading !== null}
                data-testid={`button-topup-pack-${pack.priceCents}`}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-3 rounded-lg border border-border",
                  "hover:border-primary/60 hover:bg-primary/5 transition-colors text-left",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                <div className="flex items-center gap-3">
                  <Zap className="w-4 h-4 text-primary shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{pack.label}</p>
                    <p className="text-xs text-muted-foreground">{formatCr(pack.creditsCents)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {pack.badge && (
                    <Badge variant="secondary" className="text-xs">{pack.badge}</Badge>
                  )}
                  {loading === pack.priceCents ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : (
                    <span className="text-sm font-semibold">${(pack.priceCents / 100).toFixed(0)}</span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
        <p className="text-xs text-muted-foreground text-center pb-1">
          Powered by Stripe · Secure checkout
        </p>
      </DialogContent>
    </Dialog>
  );
}

interface AiCreditPillProps {
  isAdmin: boolean;
}

export function AiCreditPill({ isAdmin }: AiCreditPillProps) {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [location] = useLocation();
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: bal, isLoading } = useQuery<CreditBalanceResp>({
    queryKey: ["/api/me/credit-balance"],
    refetchInterval: 60_000,
  });

  // Handle successful Stripe redirect back. The hosted checkout returns a
  // session_id which MUST be confirmed server-side before the balance is
  // actually credited — previously this only showed a (false) success toast.
  const topUpConfirmedRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ai_topup") !== "1") return;
    if (topUpConfirmedRef.current) return;
    topUpConfirmedRef.current = true;
    const sessionId = params.get("session_id");
    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("ai_topup");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.pathname + url.search);
    };
    (async () => {
      try {
        if (sessionId) {
          const r = await apiRequest("POST", "/api/ai-credits/checkout-session/confirm", { sessionId });
          if (!r.ok) {
            const e = await r.json().catch(() => ({}));
            throw new Error((e as any)?.message || "Could not confirm purchase");
          }
        }
        qc.invalidateQueries({ queryKey: ["/api/me/credit-balance"] });
        qc.invalidateQueries({ queryKey: ["/api/ai-credits/balance"] });
        toast({ title: "AI credits added!", description: "Your workspace balance has been updated." });
        // Only strip the params once the credit is confirmed. On failure we keep
        // session_id so a refresh retries the (idempotent) confirm rather than
        // stranding a paid-but-uncredited session.
        cleanUrl();
      } catch (err: any) {
        toast({
          title: "Purchase not confirmed",
          description: err?.message || "Please refresh; if credits are missing, contact support.",
          variant: "destructive",
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  if (isLoading || !bal) {
    return (
      <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted/50 animate-pulse h-7 w-20" />
    );
  }

  const mode = bal.ai?.mode ?? "shared";
  // Admin always sees workspace balance (no personal pool)
  const showWorkspace = isAdmin || mode === "shared" || mode === "workspace_pays";
  const rawBalance = showWorkspace
    ? (bal.ai?.workspace?.balanceCents ?? 0)
    : (bal.ai?.perUser?.currentBalance ?? 0);

  const canTopUp = isAdmin;
  const low = rawBalance < 200;

  return (
    <>
      <button
        onClick={() => canTopUp && setTopUpOpen(true)}
        disabled={!canTopUp}
        title={canTopUp ? "Top up AI credits" : "AI credits balance"}
        data-testid="button-ai-credit-pill"
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors shrink-0",
          "border",
          low
            ? "border-red-300 bg-red-50 text-red-700 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300"
            : "border-primary/20 bg-primary/8 text-primary dark:bg-primary/10",
          canTopUp && "hover:border-primary/50 hover:bg-primary/15 cursor-pointer",
          !canTopUp && "cursor-default",
        )}
      >
        <Sparkles className={cn("w-3 h-3 shrink-0", low && "text-red-500 dark:text-red-400")} />
        <span data-testid="text-ai-credit-balance">{formatCr(rawBalance)}</span>
      </button>

      {canTopUp && (
        <TopUpDialog open={topUpOpen} onOpenChange={setTopUpOpen} />
      )}
    </>
  );
}
